const Hp = require('hemera-plugin');
const HemeraJoi = require('hemera-joi');
import { NatsPubSub } from 'graphql-nats-subscriptions';
import * as Nats from 'nats';
import {
    ITerminalMicroservice,
    TYPES as terminalServiceTypes, ITerminalInstance,
    terminalModule,
} from '@xterm-stack/server-core';
import { TerminalCommands, PubsubIdentifier, NATS_HEMERA_KUBE_TERMINAL } from '@xterm-stack/core';
import * as ILogger from 'bunyan';
import { Container, interfaces } from 'inversify';
import { workbenchContainer } from '@workbench-stack/module-server';
import {
    IConfigurationService, TYPES as configTypes,
} from '@workbench-stack/configuration';
import { request } from 'graphql-request';

async function kubectlXtermManager(hemera, options: { kubeSettings: any & { workspaceId: string }, client: Nats.Client }, next) {
    const { kubeSettings, client } = options;
    const { workspaceId, subTopic } = kubeSettings;
    const pubsub = new NatsPubSub({ client, logger: hemera.log });
    hemera.log.debug('kubeSettings received : [%j]', kubeSettings);
    const topic = `${NATS_HEMERA_KUBE_TERMINAL}/${subTopic}`;
    let container = new Container();
    container.load(workbenchContainer({
        // temporarily set this path to /tmp as this path won't be necessary
        filePath: '/tmp',
        workspaceId,
        workspaceName: '',
        workspacePath: '',
        workspaceNamespace: '',
    }));
    container.load(terminalModule(kubeSettings));
    container.bind<ILogger>('Logger').toConstantValue(hemera.log);
    container.bind('PubSub').toConstantValue(pubsub);
    // bind parentContainer
    container.bind<interfaces.Container>('Container').toDynamicValue((context: interfaces.Context) => {
        return context.container;
    });
    const configuration = container.get<IConfigurationService>(configTypes.IConfigurationService);

    const query = `
    query {
      configuration(service: ["terminal"]){
        data
      }
    }
  `;
    const conf = await request<{ configuration: { data: any } }>(kubeSettings.graphqlUrl, query);

    configuration.setUserConfiguration('terminal', JSON.parse(conf.configuration.data));
    const terminalService = container.get<ITerminalMicroservice>(terminalServiceTypes.ITerminalMicroservice);


    // Hemera methods
    hemera.add({
        topic,
        cmd: TerminalCommands.CREATE_TERMINAL,
    }, async (args) => {
        hemera.log.debug('createInstance: (%j)', args);
        const instance: ITerminalInstance = await terminalService.createTerminal(args.shell, args.dimension);
        return { id: instance.id, processId: instance.processId, title: instance.title };
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.DELETE_TERMINAL,
    }, async (args) => {
        hemera.log.debug('deleteInstance: (%j)', args);
        return terminalService.deleteTerminal(args.id);
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.SEND_TEXT,
    }, async (args) => {
        hemera.log.trace('sendText: (%j)', args);
        return terminalService.sendText(args.id, args.text, args.addNewline);
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.SET_ACTIVE_TERMINAL,
    }, async (args) => {
        hemera.log.debug('setActiveInstance: (%j)', args);
        hemera.log.warn('Need to move to SET_ACTIVE_TERMINAL_BY_ID instead');
        const instance: ITerminalInstance = await terminalService.setActiveTerminal(args.terminalInstance);
        return { id: instance.id, processId: instance.processId, title: instance.title };
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.SET_ACTIVE_TERMINAL_BY_INDEX,
    }, async (args) => {
        hemera.log.debug('setActiveInstanceByIndex: (%j)', args);
        const instance: ITerminalInstance = await terminalService.setActiveTerminalByIndex(args.terminalIndex);
        return { id: instance.id, processId: instance.processId, title: instance.title };
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.SET_TERMINAL_LAYOUT,
    }, async (args) => {
        hemera.log.debug('setInstanceSize: (%j)', args);

        return terminalService.setTerminalLayout(args.id, args.dimension);
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.SET_ACTIVE_TERMINAL_TO_NEXT,
    }, async (args) => {
        const instance: ITerminalInstance = await terminalService.setActiveTerminalToNext();
        return { id: instance.id, processId: instance.processId, title: instance.title };
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.SET_ACTIVE_TERMINAL_TO_PREV,
    }, async (args) => {
        const instance: ITerminalInstance = await terminalService.setActiveTerminalToPrevious();
        return { id: instance.id, processId: instance.processId, title: instance.title };
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.GET_ACTIVE_TERMINAL_INDEX,
    }, async (args) => {
        hemera.log.debug('get activeTerminalInstanceIndex: (%j)', args);

        return terminalService.getActiveTerminalIndex();
    });
    hemera.add({
        topic,
        cmd: TerminalCommands.GET_TERMINAL_FROM_ID,
    }, async (args) => {
        hemera.log.debug('getInstanceFromId: (%j)', args);

        const instance: ITerminalInstance = await terminalService.getTerminalFromId(args.terminalId);

        return { id: instance.id, processId: instance.processId, title: instance.title };
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.GET_TERMINAL_LABLES,
    }, async (args) => {
        hemera.log.debug('getInstanceLabels: (%j)', args);

        return terminalService.getTerminalLabels();
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.GET_ACTIVE_TERMINAL,
    }, async (args) => {
        hemera.log.debug('getActiveTerminal: (%j)', args);
        const instance: ITerminalInstance = await terminalService.getActiveTerminal(args.withOutputData);
        return { id: instance.id, processId: instance.processId, title: instance.title, outputData: instance.outputData };
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.TERMINAL_INSTANCES,
    }, async (args) => {
        hemera.log.debug('get terminalInstances: (%j)', args);

        const instances = await terminalService.terminalInstances();
        return instances.map(instance => ({ id: instance.id, processId: instance.processId, title: instance.title }));
    });

    hemera.add({
        topic,
        cmd: TerminalCommands.DISPOSE,
    }, async (args) => {
        hemera.log.debug('dispose terminalInstances: (%j)', args);

        return terminalService.dispose();
    });

    next();
}

module.exports = Hp(kubectlXtermManager, {
    hemera: '>=2.0.0-0',
    name: require('../package.json').name,
});
